// src/pages/Employee/EditEmployee/index.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, CardHeader, CardTitle, Button, Spinner } from 'reactstrap';
import { ArrowLeft, ArrowRight, Save, X, Loader } from 'react-feather';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import StepIndicator from '../../Components/employee/stepindicator';
import Step1BasicInfo from '../../Components/employee/step1BasicInfo';
import Step2PersonalFamily from '../../Components/employee/step2PersonalFamily';
import Step3Address from '../../Components/employee/step3Address';
import Step4JobSalaryBank, { LookupOption } from '../../Components/employee/step4JobSalary';
import Step5Qualifications from '../../Components/employee/step5Qualification';
import Step6Documents from '../../Components/employee/step6Documents';
import Step7EmergencyReference from '../../Components/employee/step7Emergency';

import { validateStep } from '../../types/validator';
import { buildEmployeePayload } from '../../Components/employee/buildpayload';
import {
    EmployeeFormData,
    initialEmployeeFormData,
    StepErrors,
    WIZARD_STEPS,
    FileValue,
    emptyFile,
} from '../../types/employee.types';

const LAST_STEP = WIZARD_STEPS.length - 1;

const EditEmployeenewone: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const [form, setForm] = useState<EmployeeFormData>(initialEmployeeFormData);
    const [currentStep, setCurrentStep] = useState(0);
    const [furthestStep, setFurthestStep] = useState(0);
    const [stepErrors, setStepErrors] = useState<Record<number, StepErrors>>({});
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);

    // Lookup data for selects on the Job/Salary/Bank step
    const [designations, setDesignations] = useState<LookupOption[]>([]);
    const [units, setUnits] = useState<LookupOption[]>([]);
    const [employeeLocations, setEmployeeLocations] = useState<LookupOption[]>([]);
    const [banks, setBanks] = useState<LookupOption[]>([]);
    const [loadingLookups, setLoadingLookups] = useState(true);
    const [apiValidationError, setApiValidationError] = useState(false);

    const handleStepValidation = (isValid: boolean) => {
        setApiValidationError(!isValid);
    };

    // ---------- Fetch employee data ----------
    const fetchEmployeeData = useCallback(async () => {
        if (!id) {
            setFetchError('Employee ID is missing');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await apiClient.get(`/employees/${id}`);
            const employeeData = response?.data?.data || response?.data || response;

            if (!employeeData) {
                throw new Error('No employee data received');
            }
            console.log('employee data', employeeData)
            // Transform API response to form data structure
            const formData = transformApiDataToFormData(employeeData);
            setForm(formData);

            // Mark all steps as accessible since we have data
            setFurthestStep(LAST_STEP);

        } catch (error: any) {
            console.error('Error fetching employee:', error);
            setFetchError(error?.message || 'Failed to load employee data');
            toast.error('Failed to load employee data');
        } finally {
            setLoading(false);
        }
    }, [id]);

    // ---------- Transform API data to form structure ----------
    const transformApiDataToFormData = (data: any): EmployeeFormData => {
        // Helper to convert file data to FileValue
        const toFileValue = (fileData: any): FileValue => {
            if (!fileData) return emptyFile();
            return {
                file: null,
                existingUrl: fileData.url || fileData.path || null,
                name: fileData.name || fileData.originalName || null,
            };
        };
        console.log('', data.employee_location_ids)
        return {
            basicInfo: {
                name: data.name || '',
                father_name: data.father_name || '',
                mother_name: data.mother_name || '',
                gender: data.gender || '',
                date_of_birth: data.date_of_birth ? data.date_of_birth.split('T')[0] : '',
                employee_code: data.employee_code || '',
                personal_email: data.personal_email || '',
                official_email: data.official_email || '',
                phone: data.phone || '',
                phone_alternate: data.phone_alternate || '',
                aadharr: data.aadharr || '',
            },
            personalFamily: {
                marital_status: data.marital_status || '',
                spouse_name: data.spouse_name || '',
                age: data.age ? String(data.age) : '',
                dependents: (data.dependents || []).map((dep: any) => ({
                    _key: dep.id || crypto.randomUUID(),
                    name: dep.name || '',
                    age: dep.age ? String(dep.age) : '',
                    relationship: dep.relationship || '',
                    education: dep.education || '',
                    working_details: dep.working_details || '',
                })),
            },
            address: {
                present_door_no: data.present_door_no || '',
                present_street: data.present_street || '',
                present_village: data.present_village || '',
                present_taluk: data.present_taluk || '',
                present_district: data.present_district || '',
                present_state: data.present_state || '',
                present_pincode: data.present_pincode || '',
                permanent_same_as_present: data.permanent_same_as_present || false,
                permanent_door_no: data.permanent_door_no || '',
                permanent_street: data.permanent_street || '',
                permanent_village: data.permanent_village || '',
                permanent_taluk: data.permanent_taluk || '',
                permanent_district: data.permanent_district || '',
                permanent_state: data.permanent_state || '',
                permanent_pincode: data.permanent_pincode || '',
                address_proof: toFileValue(data.address_proof),
            },
            jobSalaryBank: {
                date_of_joining: data.date_of_joining ? data.date_of_joining.split('T')[0] : '',
                designation_id: data.designation_id ? String(data.designation_id) : '',
                unit_id: data.unit_id ? String(data.unit_id) : '',
                employee_type: data.employee_type || '',
                geofence: data.geofence,
                facedetection:data.facedetection,
                is_pwa:data.is_pwa,
                site_allowance:data.site_allowance,
                take_home_salary:data.take_home_salary,
                employee_location_ids: data.employee_location_ids && Array.isArray(data.employee_location_ids)
                    ? data.employee_location_ids
                    : [],
                nature_of_job: data.nature_of_job || '',
                probation_period_months: data.probation_period_months ? String(data.probation_period_months) : '6',
                contract_end_date: data.contract_end_date ? data.contract_end_date.split('T')[0] : '',
                basic_salary: data.basic_salary ? String(data.basic_salary) : '',
                pf_esi_applicable: data.pf_esi_applicable || false,
                pf_esi_gross_salary: data.pf_esi_gross_salary ? String(data.pf_esi_gross_salary) : '',
                pf_number: data.pf_number || '',
                esi_number: data.esi_number || '',
                paid_leaves: data.paid_leaves ? String(data.paid_leaves) : '',
                payment_mode: data.payment_mode || '',
                salary_calculation: data.salary_calculation || '',
                overtime: data.overtime || false,
                bank_id: data.bank_id ? String(data.bank_id) : '',
                bank_account_no: data.bank_account_no || '',
                ifsc_code: data.ifsc_code || '',
                bank_branch: data.bank_branch || '',
            },
            qualifications: {
                educations: (data.educations || []).map((edu: any) => ({
                    _key: edu.id || crypto.randomUUID(),
                    course_type: edu.course_type || '',
                    course_name: edu.course_name || '',
                    board_or_school: edu.board_or_school || '',
                    percentage: edu.percentage ? String(edu.percentage) : '',
                    from_year: edu.from_year || '',
                    to_year: edu.to_year || '',
                    duration_months: edu.duration_months ? String(edu.duration_months) : '',
                    institution: edu.institution || '',
                })),
                experiences: (data.experiences || []).map((exp: any) => ({
                    _key: exp.id || crypto.randomUUID(),
                    company_name: exp.company_name || '',
                    from_date: exp.from_date || '',
                    to_date: exp.to_date || '',
                    nature_of_work: exp.nature_of_work || '',
                    reason_for_leaving: exp.reason_for_leaving || '',
                    attachment: toFileValue(exp.attachment),
                })),
                certifications: (data.certifications || []).map((cert: any) => ({
                    _key: cert.id || crypto.randomUUID(),
                    course_name: cert.course_name || '',
                    duration_months: cert.duration_months ? String(cert.duration_months) : '',
                    percentage: cert.percentage ? String(cert.percentage) : '',
                    institution: cert.institution || '',
                    attachment: toFileValue(cert.attachment),
                })),
            },
            documents: {
                pan_number: data.pan_number || '',
                pan_attachment: toFileValue(data.pan_attachment),
                aadhar_attachment: toFileValue(data.aadhar_attachment),
                epf_uan: data.epf_uan || '',
                epf_attachment: toFileValue(data.epf_attachment),
                esic_number: data.esic_number || '',
                esic_attachment: toFileValue(data.esic_attachment),
                resume: toFileValue(data.resume),
                payslip_1: toFileValue(data.payslip_1),
                payslip_2: toFileValue(data.payslip_2),
                payslip_3: toFileValue(data.payslip_3),
            },
            emergencyReference: {
                emergency_name: data.emergency_name || '',
                emergency_relationship: data.emergency_relationship || '',
                emergency_mobile: data.emergency_mobile || '',
                emergency_email: data.emergency_email || '',
                reference_name: data.reference_name || '',
                reference_designation: data.reference_designation || '',
                reference_address: data.reference_address || '',
                reference_mobile: data.reference_mobile || '',
                reference_email: data.reference_email || '',
            },
        };
    };

    // ---------- Fetch lookups ----------
    const fetchLookups = useCallback(async () => {
        setLoadingLookups(true);
        try {
            const [designationRes, unitRes, locationRes, bankRes] = await Promise.allSettled([
                apiClient.get('/designations'),
                apiClient.get('/units'),
                apiClient.get('/locations/office/active/all'),
                apiClient.get('/banks/active'),
            ]);

            const unwrap = (res: PromiseSettledResult<any>, labelKey: string): LookupOption[] => {
                if (res.status !== 'fulfilled') return [];
                const raw = res.value?.data || res.value || [];
                const list = Array.isArray(raw) ? raw : raw.data || [];
                return list.map((item: any) => ({
                    id: item.id,
                    label: item[labelKey] || item.name || item.title || item.office_name || item.bankname
                }));
            };

            setDesignations(unwrap(designationRes, 'title'));
            setUnits(unwrap(unitRes, 'name'));
            setEmployeeLocations(unwrap(locationRes, 'name'));
            setBanks(unwrap(bankRes, 'bank_name'));
        } catch (error) {
            console.error('Error fetching lookup data:', error);
        } finally {
            setLoadingLookups(false);
        }
    }, []);

    // ---------- Initial data fetch ----------
    useEffect(() => {
        const loadData = async () => {
            await Promise.all([fetchEmployeeData(), fetchLookups()]);
        };
        loadData();
    }, [fetchEmployeeData, fetchLookups]);

    // ---------- Generic per-step data setters ----------
    const updateStepData = <K extends keyof EmployeeFormData>(key: K, value: EmployeeFormData[K]) => {
        setForm((prev: any) => ({ ...prev, [key]: value }));
    };

    const currentErrors = stepErrors[currentStep] || {};

    const stepHasError: Record<number, boolean> = {};
    Object.keys(stepErrors).forEach((k) => {
        stepHasError[Number(k)] = Object.keys(stepErrors[Number(k)]).length > 0;
    });

    const goToStep = (index: number) => {
        if (index < 0 || index > LAST_STEP) return;
        setCurrentStep(index);
    };

    const handleNext = () => {
        const errors = validateStep(currentStep, form);
        setStepErrors((prev) => ({ ...prev, [currentStep]: errors }));

        if (Object.keys(errors).length > 0) {
            toast.error('Please fix the highlighted fields before continuing');
            return;
        }

        if (apiValidationError) {
            toast.error('Please enter a valid and unique employee code');
            return;
        }

        const next = Math.min(currentStep + 1, LAST_STEP);
        setCurrentStep(next);
        setFurthestStep((prev) => Math.max(prev, next));
    };

    const handleBack = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    };

    const handleStepClick = (index: number) => {
        const errors = validateStep(currentStep, form);
        setStepErrors((prev) => ({ ...prev, [currentStep]: errors }));
        goToStep(index);
    };

    // ---------- Submit update ----------
    const handleSubmit = async () => {
        let allErrors: Record<number, StepErrors> = {};
        let firstInvalidStep: number | null = null;

        WIZARD_STEPS.forEach((_: any, index: any) => {
            const errors = validateStep(index, form);
            allErrors[index] = errors;
            if (Object.keys(errors).length > 0 && firstInvalidStep === null) {
                firstInvalidStep = index;
            }
        });

        setStepErrors(allErrors);

        if (firstInvalidStep !== null) {
            setCurrentStep(firstInvalidStep);
            setFurthestStep((prev) => Math.max(prev, firstInvalidStep as number));
            toast.error(`Please review Step ${(firstInvalidStep as number) + 1}: ${WIZARD_STEPS[firstInvalidStep as number].label}`);
            return;
        }

        setSubmitting(true);
        try {
            const payload = buildEmployeePayload(form);
            console.log("payload", payload)
            // Use PUT for update
            const response = await apiClient.patch(`/employees/${id}`, payload, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            toast.success('Employee updated successfully!');
            const data = response?.data || response;
            navigate('/employees');
        } catch (error: any) {
            console.error('Error updating employee:', error);
            toast.error(error?.message || 'Failed to update employee');
        } finally {
            setSubmitting(false);
        }
    };

    // ---------- Render step ----------
    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return <Step1BasicInfo
                    data={form.basicInfo}
                    onChange={(v: any) => updateStepData('basicInfo', v)}
                    errors={currentErrors}
                    onValidationChange={handleStepValidation}
                />;
            case 1:
                return (
                    <Step2PersonalFamily
                        data={form.personalFamily}
                        onChange={(v: any) => updateStepData('personalFamily', v)}
                        errors={currentErrors}
                        formData={form}
                    />
                );
            case 2:
                return <Step3Address
                    data={form.address}
                    onChange={(v: any) => updateStepData('address', v)}
                    errors={currentErrors}
                />;
            case 3:
                return (
                    <Step4JobSalaryBank
                        data={form.jobSalaryBank}
                        onChange={(v: any) => updateStepData('jobSalaryBank', v)}
                        errors={currentErrors}
                        designations={designations}
                        units={units}
                        employeeLocations={employeeLocations}
                        banks={banks}
                        loadingLookups={loadingLookups}
                    />
                );
            case 4:
                return (
                    <Step5Qualifications
                        data={form.qualifications}
                        onChange={(v: any) => updateStepData('qualifications', v)}
                        errors={currentErrors}
                    />
                );
            case 5:
                return <Step6Documents
                    data={form.documents}
                    onChange={(v: any) => updateStepData('documents', v)}
                    errors={currentErrors}
                />;
            case 6:
                return (
                    <Step7EmergencyReference
                        data={form.emergencyReference}
                        onChange={(v: any) => updateStepData('emergencyReference', v)}
                        errors={currentErrors}
                    />
                );
            default:
                return null;
        }
    };

    const isLastStep = currentStep === LAST_STEP;

    // ---------- Loading state ----------
    if (loading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardBody className="text-center py-5">
                                    <Loader size={48} className="text-primary mb-3" style={{ animation: 'spin 1s linear infinite' }} />
                                    <h5>Loading employee data...</h5>
                                    <p className="text-muted">Please wait while we fetch the employee information</p>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    // ---------- Error state ----------
    if (fetchError) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardBody className="text-center py-5">
                                    <X size={48} className="text-danger mb-3" />
                                    <h5 className="text-danger">Error Loading Employee</h5>
                                    <p className="text-muted">{fetchError}</p>
                                    <Button color="primary" onClick={() => navigate('/employees')}>
                                        Back to Employees
                                    </Button>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    // ---------- Main render ----------
    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Edit Employee" pageTitle="Employees" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <CardTitle tag="h5" className="mb-0">
                                    Edit Employee: {form.basicInfo.name || `#${id}`}
                                </CardTitle>
                                <Button color="secondary" size="sm" onClick={() => navigate('/employees')}>
                                    <X size={14} className="me-1" />
                                    Cancel
                                </Button>
                            </CardHeader>

                            <CardBody>
                                <StepIndicator
                                    steps={WIZARD_STEPS}
                                    currentIndex={currentStep}
                                    furthestIndex={furthestStep}
                                    stepHasError={stepHasError}
                                    onStepClick={handleStepClick}
                                />

                                <div className="border-top pt-4">{renderStep()}</div>

                                <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-4">
                                    <Button color="secondary" outline onClick={handleBack} disabled={currentStep === 0 || submitting}>
                                        <ArrowLeft size={14} className="me-1" />
                                        Back
                                    </Button>

                                    <span className="text-muted small">
                                        Step {currentStep + 1} of {WIZARD_STEPS.length}
                                    </span>

                                    {!isLastStep && (
                                        <Button color="primary" onClick={handleNext} disabled={submitting}>
                                            Next
                                            <ArrowRight size={14} className="ms-1" />
                                        </Button>
                                    )}

                                    {isLastStep && (
                                        <Button color="success" onClick={handleSubmit} disabled={submitting}>
                                            {submitting ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Updating...
                                                </>
                                            ) : (
                                                <>
                                                    <Save size={14} className="me-1" />
                                                    Update Employee
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
        </div>
    );
};

export default EditEmployeenewone;